"use client";

import { cn } from "@ui/lib";
import {
	ChevronLeftIcon,
	EllipsisVerticalIcon,
	ShoppingBasketIcon,
	XIcon,
} from "lucide-react";
import {
	createContext,
	type ReactNode,
	type RefObject,
	use,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import type { ShopperPageData } from "~/server/shopper";
import { type Copy, fill } from "./copy";
import { useCopy } from "./copy-context";
import { customFontCss } from "./font-face";
import { RetailerMark } from "./RetailerMark";
import { PAGE_SCOPE, shopperThemeCss } from "./theme";
import { type Basket, formatMoney } from "./types";

/**
 * The chat's frame: everything around the conversation. Shared by the live chat and the
 * Configure screen's preview, so the preview cannot drift from what shoppers get.
 */

/** The message box's placeholder: the retailer's, or "Ask <assistant name>…" in the chat's language. */
export function composerPlaceholder(page: ShopperPageData, copy: Copy): string {
	return (
		page.chat.placeholder.trim() ||
		fill(copy.placeholder, { name: page.assistantName })
	);
}

/**
 * Inside a chat mounted inline (`inline` on `DeviceFrame`): the element its drawer and dialogs
 * portal into, so they wear its scoped theme and stay inside it. Null on the hosted page, where
 * they portal into <body> and the page's theme covers them.
 */
const ChatLayerContext = createContext<RefObject<HTMLDivElement | null> | null>(
	null,
);

export function useChatLayer() {
	return use(ChatLayerContext);
}

/**
 * Full screen on phones and in the retailer's WebView. On a laptop the chat
 * sits in a phone-sized frame, so a demo looks like the app it will live in.
 * `inline` fills its container instead, inside one of our own pages.
 */
export function DeviceFrame({
	appearance,
	customFont,
	dir,
	native = false,
	inline = false,
	children,
}: {
	appearance: ShopperPageData["appearance"];
	/** The retailer's uploaded typeface, declared and worn by this chat. */
	customFont?: ShopperPageData["appearance"]["customFont"];
	/** `rtl` when the chat's own wording is Arabic. */
	dir?: "rtl" | "ltr";
	/**
	 * Inside a native SDK, which keeps the chat below the status bar itself. An Android
	 * WebView there can still report the status bar as a top inset (it did in a Flutter app,
	 * under the app's own toolbar), which drew a blank band above the chat.
	 */
	native?: boolean;
	/**
	 * Mounted inside another page of ours (the Configure preview, the public site's demo
	 * store): the theme is scoped to this element, which fills its container, holds its own
	 * drawer and dialogs (`contain-layout` makes it their containing block) and has no status
	 * bar to keep clear of.
	 */
	inline?: boolean;
	children: ReactNode;
}) {
	const id = useId();
	const layer = useRef<HTMLDivElement>(null);
	const scope = inline ? `[data-shop-chat="${id}"]` : PAGE_SCOPE;
	const font = customFont ? customFontCss(customFont, scope) : "";
	if (inline) {
		return (
			<div
				data-shop-chat={id}
				dir={dir}
				className="relative flex h-full w-full flex-col overflow-hidden bg-(--shop-surface) text-(--shop-ink) contain-layout"
			>
				<FrameStyle
					css={`${shopperThemeCss(appearance, scope)}${font}${scope}{--shop-safe-top:0px}`}
				/>
				<ChatLayerContext value={layer}>{children}</ChatLayerContext>
				<div ref={layer} />
			</div>
		);
	}
	// The page is an app shell, not a document: the chat and the welcome screen
	// scroll inside it. Without this the body keeps the admin's `min-h-screen`
	// (100vh, which on iOS is the height with the toolbars RETRACTED), so a
	// phone can scroll a toolbar's worth past the composer into the admin
	// background — black, on a phone in dark mode.
	const css = `${shopperThemeCss(appearance, scope)}${font}html,body{height:100dvh;overflow:hidden;overscroll-behavior:none;background:var(--shop-surface)}:root{--shop-safe-top:${native ? "0px" : "env(safe-area-inset-top, 0px)"}}`;
	return (
		<div
			dir={dir}
			className="flex h-dvh w-full items-center justify-center bg-(--shop-surface) text-(--shop-ink) md:bg-(--shop-brand-soft)"
		>
			<FrameStyle css={css} />
			<div className="relative flex h-full w-full flex-col overflow-hidden bg-(--shop-surface) md:h-[min(56rem,calc(100dvh-3rem))] md:w-[26.5rem] md:rounded-[2.75rem] md:shadow-[0_40px_90px_-30px_rgb(0_0_40/0.45)] md:ring-[10px] md:ring-(--shop-ink)">
				{children}
			</div>
		</div>
	);
}

/**
 * The frame's stylesheet. Not a text child: React escapes the quotes in font names there, and
 * a <style> element does not decode entities.
 */
function FrameStyle({ css }: { css: string }) {
	// biome-ignore lint/security/noDangerouslySetInnerHtml: built from schema-checked colours and our own constants (theme.ts)
	return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

export interface MenuItem {
	label: string;
	icon: ReactNode;
	onSelect: () => void;
}

/** The logo, the assistant's name and line, the menu and the basket. */
export function ChatHeader({
	page,
	divided,
	menu,
	itemCount,
	onBasket,
	onBack,
	onClose,
}: {
	/** Null hides the basket button (`chat.showBasket`). */
	page: ShopperPageData;
	/** The back arrow before the logo (`chat.showBack`); null hides it. */
	onBack: (() => void) | null;
	/** A hairline under the header: over a conversation, not over the welcome screen. */
	divided: boolean;
	menu: MenuItem[];
	itemCount: number;
	onBasket: (() => void) | null;
	/** The widget's own close button; null everywhere else. */
	onClose: (() => void) | null;
}) {
	const copy = useCopy();
	return (
		<header
			className={cn(
				"relative z-10 shrink-0 bg-(--shop-header) pt-(--shop-safe-top) text-(--shop-on-header) transition-shadow",
				divided && "shadow-[0_1px_0_var(--shop-brand-line)]",
			)}
		>
			<div className="mx-auto flex h-15 w-full max-w-2xl items-center gap-2.5 px-4">
				{onBack ? (
					<button
						type="button"
						aria-label={copy.back}
						onClick={onBack}
						className="-ms-2 flex size-10 shrink-0 items-center justify-center rounded-(--shop-btn) outline-none hover:bg-(--shop-on-header)/10 focus-visible:ring-3 focus-visible:ring-(--shop-on-header)/35"
					>
						<ChevronLeftIcon
							className="size-6 rtl:rotate-180"
							aria-hidden
						/>
					</button>
				) : null}
				{page.chat.showLogo ? (
					<RetailerMark
						name={page.retailerName}
						logo={page.logo}
						className="size-9 text-sm"
					/>
				) : null}
				<div className="min-w-0 flex-1 leading-tight">
					<p className="truncate font-bold text-[15px]">
						{page.assistantName}
					</p>
					{page.appearance.tagline ? (
						<p className="truncate text-xs opacity-70">
							{page.appearance.tagline}
						</p>
					) : null}
				</div>
				<HeaderMenu items={menu} />
				{onBasket ? (
					<button
						type="button"
						onClick={onBasket}
						aria-label={`${copy.labels.basketTitle}, ${copy.items(itemCount)}`}
						className={cn(
							"press flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-(--shop-btn) bg-(--shop-header-action) font-bold text-(--shop-on-header-action) text-sm outline-none focus-visible:ring-3 focus-visible:ring-(--shop-header-action)/40",
							itemCount ? "ps-3 pe-3.5" : "px-0",
						)}
					>
						<ShoppingBasketIcon className="size-4.5" aria-hidden />
						{itemCount ? (
							<span
								key={itemCount}
								className="animate-shop-bump tabular"
							>
								{itemCount}
							</span>
						) : null}
					</button>
				) : null}
				{onClose ? (
					<button
						type="button"
						aria-label={copy.closeChat}
						onClick={onClose}
						className="flex size-10 shrink-0 items-center justify-center rounded-(--shop-btn) outline-none hover:bg-(--shop-on-header)/10 focus-visible:ring-3 focus-visible:ring-(--shop-on-header)/35"
					>
						<XIcon className="size-5" aria-hidden />
					</button>
				) : null}
			</div>
		</header>
	);
}

/**
 * Header overflow: order help, start over. Rendered in place (not
 * a portal) so it keeps the retailer's theme.
 */
function HeaderMenu({ items }: { items: MenuItem[] }) {
	const copy = useCopy();
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const close = (event: Event) => {
			if (
				event instanceof KeyboardEvent
					? event.key === "Escape"
					: !root.current?.contains(event.target as Node)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("pointerdown", close);
		document.addEventListener("keydown", close);
		return () => {
			document.removeEventListener("pointerdown", close);
			document.removeEventListener("keydown", close);
		};
	}, [open]);

	if (!items.length) {
		return null;
	}
	return (
		<div ref={root} className="relative">
			<button
				type="button"
				aria-label={copy.more}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
				className={cn(
					"flex size-10 shrink-0 items-center justify-center rounded-(--shop-btn) outline-none focus-visible:ring-3 focus-visible:ring-(--shop-on-header)/35",
					open
						? "bg-(--shop-on-header)/10"
						: "hover:bg-(--shop-on-header)/10",
				)}
			>
				<EllipsisVerticalIcon className="size-5" />
			</button>
			{open ? (
				<div
					role="menu"
					className="absolute end-0 top-12 z-30 w-60 origin-top-right rounded-3xl rtl:origin-top-left bg-(--shop-card) p-1.5 text-(--shop-ink) shadow-[0_18px_48px_-12px_rgb(0_0_40/0.35)] ring-1 ring-(--shop-brand-line)"
				>
					{items.map((item) => (
						<button
							key={item.label}
							type="button"
							role="menuitem"
							onClick={() => {
								item.onSelect();
								setOpen(false);
							}}
							className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-start font-semibold text-sm outline-none hover:bg-(--shop-brand-soft) focus-visible:bg-(--shop-brand-soft) [&_svg]:size-4.5 [&_svg]:text-(--shop-brand-text)"
						>
							{item.icon}
							<span className="flex-1">{item.label}</span>
						</button>
					))}
				</div>
			) : null}
		</div>
	);
}

/** Above the message box once the basket has items: the count, the total, the way in. */
export function BasketBar({
	basket,
	label,
	onOpen,
}: {
	/** The button's wording (`chat.labels.viewBasket`). */
	label: string;
	basket: Basket;
	onOpen: () => void;
}) {
	const copy = useCopy();
	return (
		<button
			type="button"
			onClick={onOpen}
			className="press flex items-center gap-3 rounded-[calc(var(--radius)*2.75)] bg-(--shop-card) py-2 ps-2.5 pe-2 text-start outline-none ring-1 ring-(--shop-brand-line) focus-visible:ring-3 focus-visible:ring-(--shop-brand)/40"
		>
			<span className="flex size-10 items-center justify-center rounded-full bg-(--shop-accent-soft) text-(--shop-accent-ink)">
				<ShoppingBasketIcon className="size-5" aria-hidden />
			</span>
			<span className="min-w-0 flex-1 leading-tight">
				<span className="block text-(--shop-muted) text-xs tabular">
					{copy.items(basket.itemCount)}
				</span>
				<span className="block font-extrabold text-(--shop-ink) text-lg tabular">
					{formatMoney(basket.total, basket.currency)}
				</span>
			</span>
			<span className="flex h-11 items-center rounded-(--shop-btn) bg-(--shop-cta) px-5 font-bold text-(--shop-on-cta) text-sm">
				{label}
			</span>
		</button>
	);
}

/** Under the message box on the welcome screen. */
export function FooterLinks({
	privacyUrl,
	poweredBy,
}: {
	privacyUrl: string;
	/** "Powered by Hephon" (`chat.poweredBy`, Hephon's to switch off). */
	poweredBy: boolean;
}) {
	const copy = useCopy();
	const link =
		"rounded-sm outline-none hover:text-(--shop-ink) focus-visible:ring-2 focus-visible:ring-(--shop-brand)/40";
	return (
		<p className="flex justify-center gap-3 text-(--shop-muted) text-[11px]">
			{poweredBy ? (
				<a
					href="https://hephon.ai"
					target="_blank"
					rel="noopener"
					className={link}
				>
					{copy.poweredBy}{" "}
					<span className="font-semibold">Hephon</span>
				</a>
			) : null}
			<a
				href={privacyUrl}
				target="_blank"
				rel="noopener"
				className={link}
			>
				{copy.privacy}
			</a>
		</p>
	);
}

/** Tappable next questions under the latest reply. */
export function FollowUps({
	suggestions,
	rtl,
	onPick,
	onPress,
}: {
	suggestions: string[];
	/** Arabic chips read, and scroll, from the right. */
	rtl: boolean;
	onPick: (text: string) => boolean;
	/** The finger is down: the search can start before the tap lands. */
	onPress: (text: string) => void;
}) {
	return (
		<ul
			aria-label="Suggested replies"
			dir={rtl ? "rtl" : "ltr"}
			className="-mx-4 flex animate-shop-rise gap-2 overflow-x-auto px-4 py-1 no-scrollbar"
			style={{ animationDelay: "120ms" }}
		>
			{suggestions.map((text) => (
				<li key={text} className="shrink-0">
					<button
						type="button"
						dir="auto"
						onPointerDown={() => onPress(text)}
						onClick={() => onPick(text)}
						className="press h-10 rounded-(--shop-btn) bg-(--shop-card) px-4 font-semibold text-(--shop-brand-text) text-sm outline-none ring-1 ring-(--shop-brand-line) hover:bg-(--shop-brand-soft) focus-visible:ring-3 focus-visible:ring-(--shop-brand)/35"
					>
						{text}
					</button>
				</li>
			))}
		</ul>
	);
}
